import React, { useMemo, useEffect, useState } from "react";
import { motion } from "framer-motion";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import AZ from "../../assets/logos/az.png";
import YouTubeModalButton from "../Sections/YouTubeModalButton";

// ---- Palette Dorée Premium ----
const colors = {
  text: '#3B2A1A',           // marron très profond, lisible sur fond doré clair
  textSoft: '#7A5C3E',
  accent: '#C69C6D',
  goldLight: '#F5E1C8',
  goldDark: '#B88A56',
  glassBg: 'rgba(255, 248, 240, 0.45)',
  glassBorder: 'rgba(255, 255, 255, 0.7)',
  shadow: '0 20px 50px rgba(184, 138, 86, 0.2)',
};

// --- Animations Framer Motion ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.3, delayChildren: 0.2 }
  }
};

const textRevealVariants = {
  hidden: { opacity: 0, y: 60, rotateX: -30 },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: { duration: 1, ease: [0.25, 1, 0.5, 1] }
  }
};

const MainContainer3 = () => {
  const [engineReady, setEngineReady] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);

    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => setEngineReady(true));

    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Particules "poussière d'or" – flottent vers le haut avec un effet bubble au survol
  const particlesOptions = useMemo(() => ({
    background: { color: { value: "transparent" } },
    fpsLimit: 60,
    particles: {
      number: { value: isMobile ? 30 : 80, density: { enable: true, area: 800 } },
      color: { value: ["#ffffff", "#FBE19A", "#D4AF37", "#C69C6D"] },
      shape: { type: "circle" },
      opacity: {
        value: { min: 0.15, max: 0.7 },
        anim: { enable: true, speed: 1, minimumValue: 0.1, sync: false }
      },
      size: {
        value: { min: 1, max: 4 },
        random: true,
        anim: { enable: true, speed: 2, minimumValue: 0.5, sync: false }
      },
      links: { enable: false },
      move: {
        enable: true,
        speed: { min: 0.3, max: 1 },
        direction: "top",
        random: true,
        straight: false,
        outModes: { default: "out" }
      }
    },
    interactivity: {
      detectsOn: "window",
      events: {
        onHover: { enable: !isMobile, mode: "bubble" },
        resize: true
      },
      modes: {
        bubble: { distance: 200, size: 8, duration: 2, opacity: 1 }
      }
    },
    detectRetina: true
  }), [isMobile]);

  return (
    <>
      <style>{`
        .hero-gold-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding: 80px 20px;
          /* Fond dégradé doré */
          background: linear-gradient(135deg, #EAD5C0 0%, #D4B895 30%, #C69C6D 60%, #E8CFB5 100%);
          background-size: 400% 400%;
          animation: goldGradientBG 12s ease infinite;
        }

        @keyframes goldGradientBG {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* Orbes dorées floues en mouvement */
        .gold-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.7;
          z-index: 0;
          animation: floatOrb 18s ease-in-out infinite alternate;
        }
        .gold-orb-1 {
          width: 50vw; height: 50vw;
          background: #F5E1C8;
          top: -15%; left: -10%;
          animation-delay: 0s;
        }
        .gold-orb-2 {
          width: 40vw; height: 40vw;
          background: #D4B895;
          bottom: -10%; right: -5%;
          animation-delay: -6s;
        }
        .gold-orb-3 {
          width: 30vw; height: 30vw;
          background: #C69C6D;
          top: 40%; left: 60%;
          animation-delay: -12s;
        }

        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(7%, 12%) scale(1.15); }
          66% { transform: translate(-5%, 8%) scale(0.9); }
          100% { transform: translate(3%, -5%) scale(1.05); }
        }

        /* Glassmorphism du logo */
        .glass-logo-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: clamp(15px, 4vw, 30px);
          background: ${colors.glassBg};
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid ${colors.glassBorder};
          border-bottom: 1px solid rgba(255,255,255,0.4);
          border-radius: 35px;
          box-shadow: ${colors.shadow}, inset 0 2px 20px rgba(255,255,255,0.6);
          z-index: 2;
        }

        .hero-logo {
          width: clamp(120px, 20vw, 220px);
          height: auto;
          filter: drop-shadow(0 8px 16px rgba(0,0,0,0.08));
        }

        /* Typographie premium */
        .text-mask-container {
          overflow: hidden;
          padding-bottom: 10px;
        }

        .hero-title {
          font-family: 'Playfair Display', serif;
          color: ${colors.text};
          font-size: clamp(1.2rem, 3vw, 1.8rem);
          font-weight: 500;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 0;
          text-shadow: 0 2px 10px rgba(255,255,255,0.5);
        }

        .hero-title-highlight {
          display: block;
          font-size: clamp(2.5rem, 6vw, 4.5rem);
          font-weight: 700;
          letter-spacing: -0.5px;
          line-height: 1.1;
          margin: 15px 0;
          background: linear-gradient(135deg, #5C3A1E, #8B5A2B, #C69C6D, #F5E1C8, #C69C6D);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: goldTextShine 8s ease-in-out infinite;
          text-shadow: none; /* le texte est déjà en dégradé */
        }

        @keyframes goldTextShine {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* Ligne décorative */
        .gold-divider {
          width: 0;
          height: 2px;
          margin: 40px auto 0;
          background: linear-gradient(90deg, transparent, #FFF8F0, ${colors.goldDark}, #FFF8F0, transparent);
          box-shadow: 0 0 15px rgba(198, 156, 109, 0.6);
        }

        /* Particules canvas */
        #tsparticles {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
        }

        @media (max-width: 768px) {
          .hero-gold-section {
            padding: 60px 16px;
          }
        }
      `}</style>

      <section className="hero-gold-section">
        {/* Orbes dorées */}
        <div className="gold-orb gold-orb-1" />
        <div className="gold-orb gold-orb-2" />
        <div className="gold-orb gold-orb-3" />

        {/* Particules dorées */}
        {engineReady && <Particles id="tsparticles" options={particlesOptions} />}

        <div className="container" style={{ position: 'relative', zIndex: 10, maxWidth: '1000px' }}>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            className="text-center"
          >
            {/* Logo avec lévitation douce */}
            <motion.div
              variants={textRevealVariants}
              className="mb-5"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="glass-logo-wrapper">
                <img src={AZ} alt="Azyekos Logo" className="hero-logo" />
              </div>
            </motion.div>

            {/* Texte avec effet reveal */}
            <div className="text-mask-container">
              <motion.h2 variants={textRevealVariants} className="hero-title">
                Il vous suffit de nous exposer
              </motion.h2>
            </div>

            <div className="text-mask-container">
              <motion.span variants={textRevealVariants} className="hero-title-highlight">
                VOS SOUHAITS <br /> ET PROJETS
              </motion.span>
            </div>

            <div className="text-mask-container">
              <motion.h2 variants={textRevealVariants} className="hero-title">
                Et nous les concrétisons
              </motion.h2>
            </div>

            {/* Bouton YouTube et ligne décorative */}
            <motion.div
              variants={textRevealVariants}
              style={{ marginTop: '45px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <YouTubeModalButton videoId="9hnnH-xBmdc" />
              </motion.div>

              <motion.div
                className="gold-divider"
                initial={{ width: "0%" }}
                animate={{ width: "min(300px, 60%)" }}
                transition={{ duration: 1.5, delay: 1, ease: "easeInOut" }}
              />
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default MainContainer3;